import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  REIMBURSEMENT_SUBSEGMENTS,
  TARGET_MARKETS,
  TARGET_MARKET_CODES,
  REIMBURSEMENT_CELLS,
  REIMBURSEMENT_BASIS,
  REIMBURSEMENT_EXPOSURE_FLOOR,
  REIMBURSEMENT_DATA_VERSION,
  REIMBURSEMENT_REVIEW_BY,
  reimbursementPressure,
  type ReimbursementCell,
  type ReimbursementSubsegment,
  type TargetMarketCode,
} from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { ramp, textOn, rampGradient, NA_HATCH } from '../heat-ramp.js';
import { type PanelId, type TabId } from '../store.js';
import { useReimbursementMargins } from '../../api/hooks.js';

interface MarginInfo {
  medianDeltaBps: number;
  n: number;
}

// Reimbursement Pressure — subsegment × country. Per cell it scores exposure
// to public-reimbursement / tariff change: pressure = public exposure ×
// (−real tariff Δ). Fed by the CURATED, CITED reference in @hc/shared
// (REIMBURSEMENT_CELLS) — static reference data, no live feed / no API. Curated
// UK + FR + DE so far (ahead of ingest); every populated cell carries a source
// + vintage. Rows = REIMBURSEMENT_SUBSEGMENTS (the top 10 by reimbursement
// relevance — GP/pharmacy in, veterinary/optical out).

// Three views. Revenue effect (default) is the headline: exposure × real
// tariff Δ → the net %/yr real-terms revenue effect (− = headwind). Public
// exposure and Tariff Δ break out the two inputs. (Distinct from the old
// "Pressure" view, which just restated the tariff with exposure in subtext.)
type ViewKey = 'effect' | 'exposure' | 'tariff';
type CellState = 'pressure' | 'tailwind' | 'stable' | 'unrated';

// Frame ALL target markets (same as the Market Opportunity rollup); markets we
// don't yet curate render as the n/a coverage layer. Names come from
// TARGET_MARKETS, since DE/ES/IT/IE aren't in the data-model MARKET_NAMES.
const COUNTRIES: TargetMarketCode[] = [...TARGET_MARKET_CODES];
const MARKET_NAME = new Map(TARGET_MARKETS.map((m) => [m.code, m.name] as const));
const countryName = (c: string) => MARKET_NAME.get(c as TargetMarketCode) ?? c;
const STATE: Record<CellState, { glyph: string; label: string }> = {
  pressure: { glyph: '↓', label: 'Pressure' },
  tailwind: { glyph: '↑', label: 'Tailwind' },
  stable: { glyph: '→', label: 'Stable' },
  unrated: { glyph: '—', label: 'Exposure only' },
};

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
const pct = (x: number) => Math.round(x * 100) + '%';
const sgn = (x: number) => (x > 0 ? '+' : '') + x.toFixed(1) + '%';

// cream → accent — same heat ramp as the Market Opportunity panel (consistent
// palette across the two surfaces). Direction is carried by the glyph, not hue.
// ramp / textOn / rampGradient are shared with Market Opportunity — see
// workspace/heat-ramp.ts.

interface Derived {
  cell: ReimbursementCell;
  pressure: number | null;
  state: CellState;
}
function deriveState(cell: ReimbursementCell): CellState {
  if (cell.tariffReal == null) return 'unrated';
  if (cell.tariffReal <= -0.8) return 'pressure';
  if (cell.tariffReal >= 0.5) return 'tailwind';
  return 'stable';
}

export function ReimbursementPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <ReimbursementBody />
    </PanelFrame>
  );
}

// The reimbursement matrix body — reused as the "Reimbursement" lens inside the
// Indications (Market Opportunity) panel at COUNTRY altitude (both are
// subsegment × country at that altitude). No PanelFrame: the host supplies it.
export function ReimbursementBody() {
  const [view, setView] = useState<ViewKey>('effect');
  const [sel, setSel] = useState<{
    bucket: ReimbursementSubsegment;
    country: TargetMarketCode;
  } | null>(null);

  // Build the derived model from the static reference. A cell is n/a (hatched)
  // when it's absent or below the exposure floor; norms scale the heat ramp.
  const { byKey, maxPressure, maxCut } = useMemo(() => {
    const map = new Map<string, Derived>();
    let mp = 0;
    let mc = 0;
    for (const cell of REIMBURSEMENT_CELLS) {
      if (cell.exposure < REIMBURSEMENT_EXPOSURE_FLOOR) continue;
      const pressure = reimbursementPressure(cell);
      map.set(`${cell.country}|${cell.bucket}`, { cell, pressure, state: deriveState(cell) });
      if (pressure != null) mp = Math.max(mp, pressure);
      if (cell.tariffReal != null) mc = Math.max(mc, Math.max(0, -cell.tariffReal));
    }
    return { byKey: map, maxPressure: mp || 1, maxCut: mc || 1 };
  }, []);

  // Observed-margin cross-check (live, from filed P&Ls). Loads async; the cell
  // detail shows it where a bucket has a usable sample (FR-heavy — UK files
  // abbreviated accounts), else "insufficient filed P&L".
  const { data: marginData } = useReimbursementMargins();
  const marginMap = useMemo(() => {
    const m = new Map<string, MarginInfo>();
    for (const r of marginData?.margins ?? [])
      m.set(`${r.country}|${r.bucket}`, { medianDeltaBps: r.medianDeltaBps, n: r.n });
    return m;
  }, [marginData]);
  // Markets where ANY bucket cleared the route's n>=8 sample bar — drives the
  // "why is this blank" label below, instead of a hardcoded market list.
  const marginCountries = useMemo(
    () => new Set((marginData?.margins ?? []).map((r) => r.country)),
    [marginData],
  );

  // Per-view: heat value t (0..1), the cell's big value, and whether the cell
  // renders as n/a in THIS view. Heat is the headwind intensity (cut-driven);
  // the effect/tariff views hide cells with no rate series. No per-cell unit
  // label — the view toggle + footer caption already say it.
  const VIEWS: Record<
    ViewKey,
    {
      label: string;
      t: (d: Derived) => number;
      val: (d: Derived) => string;
      na: (d: Derived) => boolean;
      scale: { lo: string; hi: string; cap: string };
    }
  > = {
    effect: {
      label: 'Revenue effect',
      // heat = headwind intensity (exposure × the real cut); tailwinds stay pale.
      t: (d) => (d.pressure == null ? 0 : Math.pow(clamp(d.pressure / maxPressure, 0, 1), 0.7)),
      val: (d) => (d.cell.tariffReal == null ? 'n/a' : sgn(d.cell.exposure * d.cell.tariffReal)),
      na: (d) => d.cell.tariffReal == null,
      scale: {
        lo: 'eased',
        hi: '↓ headwind',
        cap: 'exposure × real tariff Δ · net %/yr of revenue',
      },
    },
    exposure: {
      label: 'Public exposure',
      t: (d) => d.cell.exposure,
      val: (d) => pct(d.cell.exposure),
      na: () => false,
      scale: { lo: '0%', hi: '100%', cap: 'public-funded share of revenue' },
    },
    tariff: {
      label: 'Tariff Δ',
      t: (d) =>
        d.cell.tariffReal == null
          ? 0
          : Math.pow(clamp(Math.max(0, -d.cell.tariffReal) / maxCut, 0, 1), 0.8),
      val: (d) => (d.cell.tariffReal == null ? 'n/a' : sgn(d.cell.tariffReal)),
      na: (d) => d.cell.tariffReal == null,
      scale: { lo: '+ rising', hi: '− cut', cap: 'real-terms YoY rate change' },
    },
  };

  const v = VIEWS[view];
  const selD = sel ? (byKey.get(`${sel.country}|${sel.bucket}`) ?? null) : null;

  // The panel is freely resizable — it does NOT auto-fit/lock to the grid size.
  // The matrix scrolls within whatever size the user gives the panel
  // (.rp-gridwrap overflow auto). (Was a setConstraints/setSize lock — removed
  // by request, matching the Market Opportunity panel.)

  return (
    <div className="rp-root">
      <style>{RP_CSS}</style>

      <div className="rp-toolbar">
        <div className="rp-seg">
          {(Object.keys(VIEWS) as ViewKey[]).map((k) => (
            <button
              key={k}
              type="button"
              className={k === view ? 'on' : ''}
              onClick={() => setView(k)}
            >
              {VIEWS[k].label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
      </div>

      <div className="rp-gridwrap">
        <table className="rp-tbl">
          <colgroup>
            <col style={{ width: 116 }} />
            {COUNTRIES.map((c) => (
              <col key={c} style={{ width: 66 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="rp-corner">Subsegment ↓ / Country →</th>
              {COUNTRIES.map((c) => (
                <th key={c} className="rp-colhead" title={countryName(c)}>
                  {countryName(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REIMBURSEMENT_SUBSEGMENTS.map((b) => (
              <tr key={b.key}>
                <th className="rp-rowhead">{b.label}</th>
                {COUNTRIES.map((c) => {
                  const d = byKey.get(`${c}|${b.key}`);
                  if (!d || v.na(d)) return <td key={c} className="rp-cell rp-na" />;
                  const t = v.t(d);
                  const isSel = sel?.bucket === b.key && sel?.country === c;
                  return (
                    <td
                      key={c}
                      className={'rp-cell' + (isSel ? ' sel' : '')}
                      style={{ background: ramp(t), color: textOn(t) }}
                      title={`${b.label} · ${countryName(c)}`}
                      onClick={() =>
                        setSel({ bucket: b.key as ReimbursementSubsegment, country: c })
                      }
                    >
                      <div className="rp-v">{v.val(d)}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rp-foot">
        <span className="rp-lg">
          {v.scale.lo}
          <span className="rp-ramp" style={{ background: rampGradient() }} />
          {v.scale.hi}
          <span style={{ marginLeft: 4 }}>· {v.scale.cap}</span>
        </span>
        <span className="rp-lg">glyph = tariff direction · ↓ pressure · → stable · ↑ tailwind</span>
        <span className="rp-lg">
          <span className="rp-sw rp-na" /> n/a — no public exposure / no rate series
        </span>
        <span className="rp-lg rp-note" title={REIMBURSEMENT_BASIS}>
          ⓘ <b>pressure</b> = exposure × (−real tariff Δ) · curated from cited public sources (not a
          live feed)
        </span>
        <span className="rp-lg">
          curated {REIMBURSEMENT_DATA_VERSION} · review by {REIMBURSEMENT_REVIEW_BY}
        </span>
      </div>

      {selD &&
        sel &&
        createPortal(
          <CellDetail
            d={selD}
            bucketLabel={
              REIMBURSEMENT_SUBSEGMENTS.find((b) => b.key === sel.bucket)?.label ?? sel.bucket
            }
            maxPressure={maxPressure}
            margin={marginMap.get(`${sel.country}|${sel.bucket}`) ?? null}
            marketHasMargins={marginCountries.has(sel.country)}
            onClose={() => setSel(null)}
          />,
          document.body,
        )}
    </div>
  );
}

// ---- floating cell-detail panel ------------------------------------------

function CellDetail({
  d,
  bucketLabel,
  maxPressure,
  margin,
  marketHasMargins,
  onClose,
}: {
  d: Derived;
  bucketLabel: string;
  maxPressure: number;
  margin: MarginInfo | null;
  /** True when THIS market clears the margin route's sample bar in any bucket —
   *  distinguishes "thin bucket" from "no filed P&L pairs in this market". */
  marketHasMargins: boolean;
  onClose: () => void;
}) {
  const { cell } = d;
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  function onMouseDown(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('.rp-winbtn')) return;
    const r = cardRef.current!.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPos({ left: r.left, top: r.top });
    const move = (ev: MouseEvent) => {
      if (!drag.current) return;
      setPos({
        left: Math.min(window.innerWidth - 48, Math.max(-392, ev.clientX - drag.current.dx)),
        top: Math.min(window.innerHeight - 26, Math.max(0, ev.clientY - drag.current.dy)),
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  }

  const st = STATE[d.state];
  const t = d.pressure == null ? 0 : Math.pow(clamp(d.pressure / maxPressure, 0, 1), 0.7);

  return (
    <div
      ref={cardRef}
      className="rp-card"
      style={pos ? { left: pos.left, top: pos.top, transform: 'none' } : undefined}
    >
      <style>{RP_CSS}</style>
      <div className="rp-card-chrome" onMouseDown={onMouseDown}>
        <span className="rp-wintitle">
          <span className="rp-grip">⠿</span> Reimbursement detail
        </span>
        <button className="rp-winbtn danger" onClick={onClose} aria-label="Close" type="button">
          ✕
        </button>
      </div>
      <div className="rp-card-body">
        <div className="rp-crumb">{countryName(cell.country)}</div>
        <h2 className="rp-h2">{bucketLabel}</h2>
        <div className="rp-where">National payer-system signal</div>

        <div className="rp-hero">
          <div className="rp-state" style={{ background: ramp(t), color: textOn(t) }}>
            <div className="rp-state-g">{st.glyph}</div>
            <div className="rp-state-k">{st.label}</div>
          </div>
          <div className="rp-score">
            <div className="rp-score-lab">Real revenue effect</div>
            <div className="rp-score-val">
              {cell.tariffReal == null ? 'n/a' : sgn(cell.exposure * cell.tariffReal) + ' / yr'}
            </div>
            <div className="rp-score-note">
              {cell.tariffReal == null
                ? 'exposure shown; no rate series'
                : `${pct(cell.exposure)} public-funded × real tariff ${sgn(cell.tariffReal)} — net % of revenue, real terms (− = headwind)`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <Stat k="Public exposure" sub="public-funded share" v={pct(cell.exposure)} />
          <Stat
            k="Real tariff Δ"
            sub="YoY, deflated"
            v={cell.tariffReal == null ? 'n/a' : sgn(cell.tariffReal)}
          />
          {(() => {
            if (!margin) {
              // Data-derived, not a hardcoded market list. The old
              // MARGIN_MARKETS=['GB','FR'] constant was measured INVERTED on
              // 2026-07-21: GB has ONE margin-capable filer platform-wide
              // (small-filer P&L exemption) while NL/NO/BE/FI — labelled
              // "market not yet ingested" — held 117-3,673 each and the route
              // was already serving them. If the market clears the bar in any
              // bucket, this bucket is just thin; if it appears nowhere, the
              // market has no usable filed P&L pairs at all.
              const reason = marketHasMargins
                ? 'insufficient filed P&L in this bucket'
                : 'no filed P&L pairs in this market';
              return <Stat k="Margin cross-check" sub={reason} v="—" />;
            }
            const bps = (margin.medianDeltaBps >= 0 ? '+' : '') + margin.medianDeltaBps + ' bps';
            // confirms when the observed margin direction matches the tariff signal
            let verdict = '';
            if (d.state === 'pressure')
              verdict = margin.medianDeltaBps < 0 ? ' · confirms' : ' · diverges';
            else if (d.state === 'tailwind')
              verdict = margin.medianDeltaBps >= 0 ? ' · confirms' : ' · diverges';
            return (
              <Stat
                k="Margin cross-check"
                sub={`median ΔYoY, ${margin.n} filers${verdict}`}
                v={bps}
              />
            );
          })()}
        </div>

        <div className="rp-prov">
          <div className="rp-prov-row">
            <span className="rp-chip">{cell.vintage}</span>{' '}
            <span className="rp-chip">curated · cited</span>
          </div>
          <div className="rp-prov-src">{cell.source}</div>
          <div className="rp-prov-note">{cell.note}</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, sub, v }: { k: string; sub?: string; v: React.ReactNode }) {
  return (
    <div className="rp-stat">
      <span className="rp-stat-k">
        {k}
        {sub ? <span className="rp-stat-sub">{sub}</span> : null}
      </span>
      <span className="rp-stat-v">{v}</span>
    </div>
  );
}

// ---- scoped CSS (mirrors the Market Opportunity panel) -------------------

const RP_CSS = `
.rp-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.rp-toolbar { display:flex; align-items:center; gap:var(--s-2); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); flex-wrap:wrap; }
.rp-seg { display:inline-flex; border:1px solid var(--hairline); border-radius:4px; overflow:hidden; background:var(--menu-surface); }
.rp-seg button { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-secondary); background:transparent; border:none; border-right:1px solid var(--hairline); padding:4px 10px; cursor:pointer; white-space:nowrap; }
.rp-seg button:last-child { border-right:none; }
.rp-seg button.on { background:var(--accent); color:var(--bg); font-weight:600; }
.rp-seg button:not(.on):hover { background:var(--hover-subtle); }
.rp-gridwrap { flex:1; overflow:auto; padding:var(--s-3); min-height:0; }
.rp-tbl { border-collapse:separate; border-spacing:2px; width:max-content; table-layout:fixed; }
.rp-tbl th, .rp-tbl td { padding:0; }
.rp-corner { text-align:left; vertical-align:bottom; font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; padding:0 8px 6px 4px; white-space:normal; line-height:1.2; width:116px; min-width:116px; }
.rp-colhead { vertical-align:bottom; text-align:center; padding:0 3px 5px; font-size:var(--t-meta); font-weight:600; color:var(--text-secondary); text-transform:uppercase; letter-spacing:.02em; line-height:1.2; white-space:normal; overflow-wrap:anywhere; width:66px; min-width:66px; }
.rp-rowhead { text-align:left; vertical-align:middle; font-size:var(--t-body); font-weight:500; color:var(--text-primary); padding:0 12px 0 4px; white-space:normal; line-height:1.2; width:116px; min-width:116px; }
.rp-cell { width:66px; min-width:66px; height:36px; vertical-align:middle; text-align:center; border-radius:3px; cursor:pointer; border:1px solid rgba(26,20,16,.05); outline:2px solid transparent; outline-offset:-1px; }
.rp-cell .rp-v { font-size:12px; font-weight:600; line-height:1; white-space:nowrap; padding:0 5px; }
.rp-cell:hover { outline-color:var(--text-primary); }
.rp-cell.sel { outline-color:var(--accent); }
.rp-cell.rp-na { background:${NA_HATCH}; cursor:default; }
.rp-foot { border-top:1px solid var(--hairline); padding:8px var(--s-3); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.4; display:flex; gap:0 16px; flex-wrap:wrap; align-items:baseline; }
.rp-foot b { color:var(--text-secondary); font-weight:600; }
.rp-lg { display:inline-flex; align-items:center; gap:4px; white-space:nowrap; }
.rp-note { font-style:italic; color:var(--text-secondary); cursor:help; white-space:normal; }
.rp-ramp { display:inline-block; height:8px; width:90px; border-radius:2px; border:1px solid rgba(26,20,16,.15); margin:0 4px; }
.rp-sw { display:inline-block; width:14px; height:9px; border-radius:2px; border:1px solid rgba(26,20,16,.15); }
.rp-card { position:fixed; left:50%; top:84px; transform:translateX(-50%); width:420px; max-width:calc(100vw - 24px); max-height:calc(100vh - 24px); overflow:auto; background:var(--surface); border:1px solid var(--panel-border); border-radius:4px; box-shadow:0 6px 16px rgba(89,64,41,.18),0 14px 40px rgba(26,20,16,.2); z-index:9500; }
.rp-card-chrome { height:26px; background:var(--surface-elevated); border-bottom:1px solid var(--hairline); display:flex; align-items:center; justify-content:space-between; padding:0 4px 0 var(--s-2); font-size:var(--t-meta); letter-spacing:.06em; text-transform:uppercase; color:var(--text-secondary); font-weight:600; cursor:grab; user-select:none; }
.rp-card-chrome:active { cursor:grabbing; }
.rp-wintitle { display:inline-flex; align-items:center; gap:6px; }
.rp-grip { color:var(--text-tertiary); font-size:11px; letter-spacing:-2px; }
.rp-winbtn { width:20px; height:18px; display:inline-flex; align-items:center; justify-content:center; border:1px solid transparent; background:transparent; color:var(--text-tertiary); font-size:12px; cursor:pointer; border-radius:2px; padding:0; }
.rp-winbtn:hover { background:var(--hover-subtle); color:var(--text-primary); border-color:var(--hairline); }
.rp-winbtn.danger:hover { color:var(--alert); border-color:var(--alert); }
.rp-card-body { padding:var(--s-3) var(--s-4) var(--s-4); }
.rp-crumb { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; }
.rp-h2 { margin:3px 0 1px; font-size:var(--t-page-title); font-weight:700; letter-spacing:-0.01em; }
.rp-where { font-size:var(--t-body); color:var(--text-secondary); }
.rp-hero { margin-top:12px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:5px; padding:var(--s-3); display:flex; align-items:center; gap:14px; }
.rp-state { width:60px; height:60px; border-radius:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; flex-shrink:0; }
.rp-state-g { font-size:26px; font-weight:700; line-height:1; }
.rp-state-k { font-size:7.5px; text-transform:uppercase; letter-spacing:.06em; opacity:.9; margin-top:2px; text-align:center; }
.rp-score { flex:1; }
.rp-score-lab { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; }
.rp-score-val { font-size:var(--t-section); font-weight:600; margin-top:2px; }
.rp-score-note { font-size:var(--t-meta); color:var(--text-tertiary); margin-top:2px; }
.rp-stat { display:flex; justify-content:space-between; align-items:baseline; padding:8px 0; border-bottom:1px solid var(--hairline); gap:10px; }
.rp-stat-k { font-size:var(--t-body); color:var(--text-secondary); }
.rp-stat-sub { display:block; font-size:var(--t-meta); color:var(--text-tertiary); }
.rp-stat-v { font-size:var(--t-section); font-weight:600; text-align:right; white-space:nowrap; }
.rp-prov { margin-top:12px; border:1px solid var(--hairline); border-radius:4px; padding:7px 10px; }
.rp-chip { display:inline-block; font-size:var(--t-meta); padding:1px 6px; border-radius:3px; border:1px solid var(--hairline); font-weight:500; color:var(--text-tertiary); letter-spacing:.02em; }
.rp-prov-src { font-size:var(--t-meta); color:var(--text-secondary); margin-top:6px; line-height:1.45; }
.rp-prov-note { font-size:var(--t-meta); color:var(--text-tertiary); margin-top:5px; line-height:1.45; }
`;
